export interface MapaCalorOverlayOptions {
  radius?: number;
  opacity?: number;
}

export interface MapaCalorOverlay {
  setData(data: google.maps.LatLng[]): void;

  remove(): void;
}

export function criarMapaCalorOverlay(
  map: google.maps.Map,
  options?: MapaCalorOverlayOptions
): MapaCalorOverlay {
  const radius = options?.radius ?? 25;
  const opacity = options?.opacity ?? 0.8;

  class Overlay extends google.maps.OverlayView {
    private readonly canvas: HTMLCanvasElement;
    private data: google.maps.LatLng[] = [];
    private readonly palette: [number, number, number][];

    constructor() {
      super();
      this.canvas = document.createElement('canvas');
      this.canvas.style.position = 'absolute';
      this.canvas.style.pointerEvents = 'none';
      this.palette = this.criarPaleta();
      this.setMap(map);
    }

    override onAdd(): void {
      this.getPanes()!.overlayLayer.appendChild(this.canvas);
    }

    override draw(): void {
      const projection = this.getProjection();
      if (!projection) return;
      const bounds = map.getBounds();
      if (!bounds) return;

      const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast())!;
      const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest())!;

      const left = Math.min(sw.x, ne.x);
      const top = Math.min(ne.y, sw.y);
      const width = Math.abs(ne.x - sw.x);
      const height = Math.abs(sw.y - ne.y);

      this.canvas.style.left = `${left}px`;
      this.canvas.style.top = `${top}px`;
      this.canvas.width = width;
      this.canvas.height = height;

      if (this.data.length === 0) return;

      const ctx = this.canvas.getContext('2d')!;
      ctx.clearRect(0, 0, width, height);

      for (const latlng of this.data) {
        const pixel = projection.fromLatLngToDivPixel(latlng);
        if (!pixel) continue;
        const x = pixel.x - left;
        const y = pixel.y - top;
        const grad = ctx.createRadialGradient(x, y, 0, x, y, radius);
        grad.addColorStop(0, 'rgba(0,0,0,1)');
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = grad;
        ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
      }

      const imageData = ctx.getImageData(0, 0, width, height);
      const d = imageData.data;
      for (let i = 3; i < d.length; i += 4) {
        const v = d[i];
        if (v > 0) {
          const [r, g, b] = this.palette[v];
          d[i - 3] = r;
          d[i - 2] = g;
          d[i - 1] = b;
          d[i] = Math.round(v * opacity);
        }
      }
      ctx.putImageData(imageData, 0, 0);
    }

    override onRemove(): void {
      this.canvas.remove();
    }

    setData(data: google.maps.LatLng[]): void {
      this.data = data;
      this.draw();
    }

    remove(): void {
      this.setMap(null);
    }

    private criarPaleta(): [number, number, number][] {
      const palCanvas = document.createElement('canvas');
      palCanvas.width = 256;
      palCanvas.height = 1;
      const ctx = palCanvas.getContext('2d')!;
      const grad = ctx.createLinearGradient(0, 0, 256, 0);
      grad.addColorStop(0, '#00e400');
      grad.addColorStop(0.6, '#ffff00');
      grad.addColorStop(0.85, '#ff8000');
      grad.addColorStop(1, '#ff0000');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 256, 1);
      const data = ctx.getImageData(0, 0, 256, 1).data;
      return Array.from({length: 256}, (_, i) => [data[i * 4], data[i * 4 + 1], data[i * 4 + 2]]);
    }
  }

  return new Overlay();
}
